export const FLOW_CONTEXT_VERSION = "1.0";
export const GENERATION_IR_VERSION = "1.0";
export const FLOW_PATCH_VERSION = "1.0";

const FLOW_PATCH_OPS = new Set([
    "addMotion",
    "addNode",
    "connect",
    "disconnect",
    "insertNodeAfter",
    "removeNode",
    "setNodeData"
]);

function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}

function isObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

// anchorRef may be a non-empty string id or an object selector
// ({id|nodeId} | {motionName,...} | {nodeType,...}).
function isValidAnchorRef(ref) {
    if (isNonEmptyString(ref)) return true;
    if (!isObject(ref)) return false;
    return (
        isNonEmptyString(ref.id) ||
        isNonEmptyString(ref.nodeId) ||
        isNonEmptyString(ref.motionName) ||
        isNonEmptyString(ref.nodeType)
    );
}

// Deep per-op contract check. Shallow op-name validation is insufficient:
// e.g. {"operations":[{"op":"connect"}]} must NOT pass. The patch engine
// (src/agent/flow-patch.js) repeats these checks at apply time with
// structured errors; this validator is the server-facing gate that decides
// whether a generated patch is safe to return / persist.
function isValidFlowPatchOp(op) {
    if (!isObject(op) || !isNonEmptyString(op.op) || !FLOW_PATCH_OPS.has(op.op)) {
        return false;
    }
    switch (op.op) {
        case "addMotion":
            return isNonEmptyString(op.axisName) && isObject(op.motion) && isNonEmptyString(op.motion.name);
        case "addNode":
            if (!isObject(op.node) || !isNonEmptyString(op.node.type)) return false;
            if (op.node.type === "motion") {
                return (
                    isNonEmptyString(op.node.axisName) &&
                    (isNonEmptyString(op.node.motionId) || isNonEmptyString(op.node.motionName))
                );
            }
            return true;
        case "connect":
            return isNonEmptyString(op.from) && isNonEmptyString(op.to);
        case "disconnect":
            return isNonEmptyString(op.from);
        case "insertNodeAfter":
            return isValidAnchorRef(op.anchorRef) && isNonEmptyString(op.newNodeId);
        case "removeNode":
            return isNonEmptyString(op.nodeId);
        case "setNodeData":
            return isNonEmptyString(op.nodeId) && isObject(op.patch);
        default:
            return false;
    }
}

export function validateFlowPatchContract(patch) {
    return Boolean(
        patch &&
        typeof patch === "object" &&
        (patch.flowPatchVersion === undefined || patch.flowPatchVersion === FLOW_PATCH_VERSION) &&
        Array.isArray(patch.operations) &&
        patch.operations.every(isValidFlowPatchOp)
    );
}

export function validateFlowContextContract(context) {
    return Boolean(
        context &&
        typeof context === "object" &&
        typeof context.contractVersion === "string" &&
        Array.isArray(context.axes) &&
        Array.isArray(context.flowNodes) &&
        Array.isArray(context.conditions) &&
        Array.isArray(context.errors)
    );
}

export function validateGenerationIrContract(ir) {
    return Boolean(
        ir &&
        typeof ir === "object" &&
        typeof ir.contractVersion === "string" &&
        Array.isArray(ir.steps) &&
        Array.isArray(ir.conditions) &&
        Array.isArray(ir.errors)
    );
}
